import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  TextField,
  InputAdornment,
  Tooltip
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import WorkIcon from '@mui/icons-material/Work';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import { Hotel } from '../../../types/hotel';
import { User } from '../../../types/user';
import { APIHOST } from '../../../common';
import { useApp } from '../../../components/AppContext';
import InviteHotelAdmin from '../../../components/admin/inviteHotelAdmin';
import { getToken } from '../../../components/auth/AuthContext';

interface HotelAdminsProps {
  open: boolean;
  onClose: () => void;
  hotelId: string | null;
}


export default function HotelAdmins({ open, onClose, hotelId }: HotelAdminsProps) {
  const [hotel, setHotel] = useState<Hotel | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchResults, setSearchResults] = useState<User[]>([]);
  const [searching, setSearching] = useState(false);
  const [addingUser, setAddingUser] = useState<number | null>(null);
  const [removingUser, setRemovingUser] = useState<number | null>(null);
  const [showInviteDialog, setShowInviteDialog] = useState(false);

  const { setSnackbarInput } = useApp();

  useEffect(() => {
    if (open && hotelId) {
      fetchHotelWithAdmins();
    } else {
      setHotel(null);
      setError('');
      setSearchQuery('');
      setSearchResults([]);
    }
  }, [open, hotelId]);

  useEffect(() => {
    if (searchQuery.trim()) {
      const timeoutId = setTimeout(() => {
        searchUsers();
      }, 300); // Debounce search
      return () => clearTimeout(timeoutId);
    } else {
      setSearchResults([]);
    }
  }, [searchQuery]);

  const fetchHotelWithAdmins = async () => {
    if (!hotelId) return;

    try {
      setLoading(true);
      setError('');

      const token = getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(
        `${APIHOST}/api/hotels/${hotelId}?populate[admins][fields][0]=id&populate[admins][fields][1]=username&populate[admins][fields][2]=email&populate[admins][fields][3]=firstName&populate[admins][fields][4]=lastName&populate[admins][populate]=role`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch hotel administrators');
      }

      const data = await response.json();
      setHotel(data.data || null);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load hotel administrators');
      console.error('Hotel admins error:', err);
    } finally {
      setLoading(false);
    }
  };

  const searchUsers = async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      setSearching(true);
      const token = getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      const params = new URLSearchParams({
        'filters[$or][0][username][$containsi]': searchQuery.trim(),
        'filters[$or][1][email][$containsi]': searchQuery.trim(),
        'filters[$or][2][firstName][$containsi]': searchQuery.trim(),
        'filters[$or][3][lastName][$containsi]': searchQuery.trim(),
      });

      const response = await fetch(`${APIHOST}/api/users?${params}&pagination[pageSize]=5&pagination[page]=1&populate=role`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to search users');
      }

      const data: User[] = await response.json();
      // Filter out users who are already admins for this hotel and internal-role users
      const existingAdminIds = hotel?.admins?.map(admin => admin.id) || [];
      const filteredResults = data.filter((user) => {
        const roleName = user.role?.name;
        return !existingAdminIds.includes(user.id) && roleName !== 'Internal';
      });
      setSearchResults(filteredResults.slice(0, 5)); // Limit to top 5 results

    } catch (err) {
      console.error('Search error:', err);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const addAdmin = async (user: User) => {
    if (!hotelId || !hotel) return;

    try {
      setAddingUser(user.id);
      const token = getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${APIHOST}/api/hotels/${hotelId}/add-admin`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: user.documentId
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to add administrator');
      }

      // Refresh hotel data to show updated admin list
      await fetchHotelWithAdmins();
      
      // Remove user from search results and clear search
      setSearchResults(prev => prev.filter(u => u.id !== user.id));
      setSearchQuery('');
      
      setSnackbarInput({
        message: `${user.firstName} ${user.lastName} added as administrator`,
        severity: 'success',
      });
    } catch (err) {
      console.error('Add admin error:', err);
      setSnackbarInput({
        message: 'Failed to add administrator',
        severity: 'error',
      });
    } finally {
      setAddingUser(null);
    }
  };

  const removeAdmin = async (user: User) => {
    if (!hotelId || !hotel) return;

    try {
      setRemovingUser(user.id);
      const token = getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Remove user from hotel's admins list
      const currentAdminIds = hotel.admins?.map(admin => admin.id) || [];
      const updatedAdminIds = currentAdminIds.filter(id => id !== user.id);

      const response = await fetch(`${APIHOST}/api/hotels/${hotelId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          data: {
            admins: updatedAdminIds
          }
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to remove administrator');
      }

      // Refresh hotel data to show updated admin list
      await fetchHotelWithAdmins();
      
      setSnackbarInput({
        message: `${user.firstName} ${user.lastName} removed as administrator`,
        severity: 'success',
      });

    } catch (err) {
      console.error('Remove admin error:', err);
      setSnackbarInput({
        message: 'Failed to remove administrator',
        severity: 'error',
      });
    } finally {
      setRemovingUser(null);
    }
  };

  const getUserTypeIcon = (userType?: string) => {
    switch (userType) {
      case 'admin':
        return <AdminPanelSettingsIcon />;
      case 'internal':
        return <WorkIcon />;
      default:
        return <PersonIcon />;
    }
  };

  const getUserTypeColor = (userType?: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (userType) {
      case 'admin':
        return 'error';
      case 'internal':
        return 'warning';
      default:
        return 'default';
    }
  };

  const handleClose = () => {
    setHotel(null);
    setError('');
    setSearchQuery('');
    setSearchResults([]);
    onClose();
  };

  const handleOpenInviteDialog = () => {
    setShowInviteDialog(true);
  };

  const handleCloseInviteDialog = () => {
    setShowInviteDialog(false);
  };


  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" component="div">
          Hotel Administrators
        </Typography>
        {hotel && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {hotel.longName}
          </Typography>
        )}
      </DialogTitle>
      
      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress size={40} />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Loading administrators...
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Search for users to add as admins */}
        {!loading && !error && hotel && (
          
          <Box sx={{ mb: 3 }}>
            <Box sx={{display: "flex", justifyContent: "flex-end", mb: 3}}>
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleOpenInviteDialog}
                >
                  Invite New User
                </Button>
                <Tooltip title={<span>Use this for users who do not have an account yet. This will send them an email invitation to create an account and become an admin for this hotel.<br /><br /> If the user already has an account, just add them through the search box below.</span>} placement="top">
                  <InfoIcon color="action" sx={{ ml: 0.5 }} />
                </Tooltip>
              </Box>
      
            
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Select from existing users.
                <Tooltip title={<span>Use this for users who already have an account. This is useful for adding a user to multiple hotels.</span>} placement="top">
                  <InfoIcon color="action" sx={{ ml: 0.5 }} />
                </Tooltip>
              </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Search users by name, username, or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 1 }}
            />
            <Alert severity="warning" sx={{ mb: 2 }}>
              Using this tool will turn users into a hotel administrator. DO NOT apply to your accounts, CWHS employee accounts, or end user accounts. This will lock you/them out of other parts of the system, since hotel admins have very limited permissions.
            </Alert>
            {/* Search Results */}
            {searching && (
              <Box sx={{ display: 'flex', alignItems: 'center', py: 1 }}>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Searching...
                </Typography>
              </Box>
            )}
            
            {searchResults.length > 0 && (
              <List dense sx={{ bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                {searchResults.map((user) => (
                  <ListItem
                    key={user.id}
                    secondaryAction={
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() => addAdmin(user)}
                        disabled={addingUser === user.id}
                        sx={{ minWidth: 'auto' }}
                      >
                        {addingUser === user.id ? 'Adding...' : 'Add'}
                      </Button>
                    }
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'secondary.main', width: 32, height: 32 }}>
                        {getUserTypeIcon(user.userType)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2">
                            {user.firstName} {user.lastName}
                          </Typography>
                          <Chip
                            label={user.role?.name || 'user'}
                            color={getUserTypeColor(user.userType)}
                            size="small"
                          />
                        </Box>
                      }
                      secondary={`${user.email}`}
                    />
                  </ListItem>
                ))}
              </List>
            )}
            
            {searchQuery.trim() && !searching && searchResults.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                No users found matching "{searchQuery}"
              </Typography>
            )}
            
            <Divider sx={{ mt: 2 }} />
          </Box>
        )}

        {/* Current Administrators */}
        {!loading && !error && hotel && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Current Administrators
            </Typography>
            {hotel.admins && hotel.admins.length > 0 ? (
              <List disablePadding>
                {hotel.admins.map((user, index) => (
                  <React.Fragment key={user.id}>
                    <ListItem 
                      sx={{ px: 0 }}
                      secondaryAction={
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<DeleteIcon />}
                          onClick={() => removeAdmin(user as User)}
                          disabled={removingUser === user.id}
                          sx={{ minWidth: 'auto' }}
                        >
                          {removingUser === user.id ? 'Removing...' : 'Remove'}
                        </Button>
                      }
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          {getUserTypeIcon(user.userType)}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle2">
                              {user.firstName} {user.lastName}
                            </Typography>
                            <Chip
                              label={user.role?.name || 'user'}
                              color={getUserTypeColor(user.role?.name)}
                              size="small"
                            />
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              @{user.username}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {user.email}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                    {/* {index < hotel.users_permissions_users.length - 1 && <Divider />} */}
                  </React.Fragment>
                ))}
              </List>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  No administrators assigned to this hotel.
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} variant="outlined">
          Close
        </Button>
      </DialogActions>

      </Dialog>
    {hotelId ? (
      <InviteHotelAdmin
        open={showInviteDialog}
        onClose={handleCloseInviteDialog}
        hotelId={hotelId}
        onInvited={fetchHotelWithAdmins}
      />
    ) : null}
    </>
  );
}